import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TLabelTicket } from '../types/label-ticket.type';

export type TGetListLabelTicketParams = FetchParams;
export type TGetListLabelTicketResponse = ApiResponse<IPagination<TLabelTicket>>;

export const getListLabelTicket = async (params: TGetListLabelTicketParams): Promise<TGetListLabelTicketResponse> => {
  const url = '/label-ticket';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_LABEL_TICKET_QUERY_KEY = 'get-list-label-ticket';

export const listLabelTicketQueryOptions = <TData = IPagination<TLabelTicket>>(
  params: TGetListLabelTicketParams,
  select?: (data: TGetListLabelTicketResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LABEL_TICKET_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListLabelTicket(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListLabelTicketQuery<TData>(options: {
  params: TGetListLabelTicketParams;
  queryConfig?: QueryConfig<typeof listLabelTicketQueryOptions<TData>>;
  select: (data: TGetListLabelTicketResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListLabelTicketQuery(options: {
  params: TGetListLabelTicketParams;
  queryConfig?: QueryConfig<typeof listLabelTicketQueryOptions<TGetListLabelTicketResponse>>;
}): ReturnType<typeof useQuery<TGetListLabelTicketResponse>>;

// Implementation
export function useListLabelTicketQuery<TData = TGetListLabelTicketResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListLabelTicketParams;
  queryConfig?: QueryConfig<typeof listLabelTicketQueryOptions<TData | TGetListLabelTicketResponse>>;
  select?: (data: TGetListLabelTicketResponse) => TData;
}) {
  return useQuery({
    ...listLabelTicketQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListLabelTicketResponse ? TGetListLabelTicketResponse : TData>>;
}
